<template>
  <AdminLayout page="Alert Templates" title="Alert Templates">
    <div v-if="isLoading"><Loading /></div>
    <div v-else>
      <div v-if="hasPermission(PermissionType.SendAlerts)">
        <BackButton :to="`/${bldgStore.siteCode}/admin/alert`">
          Back to Alert
        </BackButton>
        <div v-if="templates && templates.length > 0" class="mt-8">
          <AddButton @click="addClick">Add Template</AddButton>
          <div
            v-for="template in templates"
            :key="template.templateId"
            class="px-4 py-2 pt-3 my-2 bg-gray-100 border-t cursor-pointer hover:bg-teal-200"
            @click="editClick(template)"
          >
            {{ template.name }}
          </div>
        </div>
        <div v-else class="mt-8 mb-2">
          <span>-- No Templates found for this building --</span>
        </div>
        <AddButton @click="addClick">Add Template</AddButton>

        <EditPopup
          entity-name="Template"
          :is-visible="isPopupVisible"
          :is-edit-mode="isEditing"
          :save-error="saveError"
          :delete-enabled="true"
          :save-on-enter="false"
          :close-on-outside-click="false"
          @close="isPopupVisible = false"
          @save="saveClick"
          @delete="deleteClick"
        >
          <BaseInput
            type="text"
            v-model="templateName"
            label="Name"
            placeholder="Template Name"
            class="mb-2"
            :error="errors.templateName"
          />
          <BaseTextarea
            class="h-64 mb-8"
            v-model="templateText"
            label="Text"
            placeholder="Template Text"
            :allow-resize="false"
            :error="errors.templateText"
          />
        </EditPopup>
      </div>
      <div v-else>You need permission to view this page.</div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useUserStore } from "../../store/userStore";
import {
  PermissionType,
  SaveTemplateRequest,
  TemplateDTO,
} from "../../store/generatedFetchClient";
import { useAlertStore } from "../../store/alertStore";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

const bldgStore = useBldgStore();
const userStore = useUserStore();
const alertStore = useAlertStore();

// List of alert templates
var templates = computed(() => alertStore.templates);
// Current property object
var property = computed(() => bldgStore.building);
const hasPermission = userStore.hasPermission;
var isLoading = ref(false);

onMounted(loadData);
watch(property, loadData);

// Load appropriate data on startup
async function loadData() {
  isLoading.value = true;
  if (property?.value?.portalId) {
    await alertStore.getTemplates(property.value.portalId);
  }
  isLoading.value = false;
}

// Popup
let isEditing = ref(false);
let isPopupVisible = ref(false);
let saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    templateName: yup.string().required().max(50).label("Name"),
    templateText: yup.string().required().label("Text"),
  }),
  initialValues: {
    templateName: "",
    templateText: "",
  },
});

let templateId = ref(-1);
const { value: templateName } = useField<string>("templateName");
const { value: templateText } = useField<string>("templateText");

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

// Called when form data is valid on save
async function onValidSubmit() {
  let saveTemplate = {
    templateId: templateId.value,
    portalId: property.value?.portalId!,
    name: templateName.value,
    text: templateText.value,
    type: "Alert", // Hardcode type for now; convert to enum later?
  } as SaveTemplateRequest;

  saveError.value = await alertStore.saveTemplate(saveTemplate);
  if (!saveError.value) {
    isPopupVisible.value = false;
    await alertStore.getTemplates(property.value?.portalId!);
  }
}

// Called when invalid form data exists on save
function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
}

// Add new template
let addClick = function () {
  templateId.value = 0;
  templateName.value = "";
  templateText.value = "";

  saveError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

// Edit existing template
let editClick = function (selected: TemplateDTO) {
  templateId.value = selected.templateId ?? 0;
  templateName.value = selected.name ?? "";
  templateText.value = selected.template ?? "";

  saveError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

// Delete existing template
async function deleteClick() {
  saveError.value = await alertStore.deleteTemplate(
    templateId.value,
    property.value?.portalId!
  );

  if (!saveError.value) {
    isPopupVisible.value = false;
    await alertStore.getTemplates(property.value?.portalId!);
  }
}
</script>
